// Fixed renderer labels. Hosts pass translated values (POS builds them with _() so the
// strings go through Loco); agent-written prose is never translated, it stays in doc.locale.
import type { Severity } from './tokens.ts'
import { CATALOG_VALUE_LABELS } from './catalog-labels.ts'

export interface MaxLabels {
  severity: Record<Severity, string>
  kind: Record<string, string>
  generated: string
  period: string
  showTable: string
  hideTable: string
  showAllRows: string
  showFewerRows: string
  rowsHidden: string
  fix: string
  affected: string
  all: string
  effort: Record<'quick' | 'moderate' | 'involved', string>
  impact: Record<'high' | 'medium' | 'low', string>
  degraded: string
  loadingChart: string
  chartFailed: string
  category: string
  value: string
  share: string
  series: string
  from: string
  to: string
  date: string
  open: string
  close: string
  low: string
  high: string
  conversion: string
  opensInNewTab: string
  noFindings: string
  diagramFailed: string
  copy: string
  copied: string
  selected: string
  artifact: Record<'source_johku_staff' | 'source_schedule' | 'source_merchant' | 'dismiss' | 'restore' | 'dismissed' | 'unread', string>
  ui: Record<'selectAll' | 'selectRow' | 'noRows' | 'working' | 'actions' | 'confirm' | 'cancel' | 'failed' | 'previous' | 'next' | 'more' | 'typeToSearch' | 'starting' | 'ending' | 'ongoing' | 'today' | 'increase' | 'decrease' | 'chooseDate', string>
  /** paymentLink → Payment link; hosts fill this with _() */
  catalog: Record<string, string>
  [key: string]: unknown
}

export const DEFAULT_LABELS: MaxLabels = {
  severity: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' },
  kind: { seo_audit: 'SEO audit', growth_audit: 'Growth audit', audit: 'Audit', report: 'Report', dashboard: 'Dashboard', interface: 'Tool', health: 'Shop health', custom: 'Johku Max' },
  generated: 'Generated',
  period: 'Period',
  showTable: 'Show data table',
  hideTable: 'Hide data table',
  showAllRows: 'Show all rows',
  showFewerRows: 'Show fewer rows',
  rowsHidden: '{count} more rows',
  fix: 'How to fix',
  affected: 'Affected',
  all: 'All',
  effort: { quick: 'Quick fix', moderate: 'Moderate effort', involved: 'Larger task' },
  impact: { high: 'High impact', medium: 'Medium impact', low: 'Low impact' },
  degraded: 'This part of the report could not be displayed.',
  loadingChart: 'Loading chart',
  chartFailed: 'The chart could not be drawn. The data is shown as a table instead.',
  category: 'Category',
  value: 'Value',
  share: 'Share',
  series: 'Series',
  from: 'From',
  to: 'To',
  date: 'Date',
  open: 'Open',
  close: 'Close',
  low: 'Low',
  high: 'High',
  conversion: 'Conversion',
  opensInNewTab: 'opens in a new tab',
  noFindings: 'No findings.',
  diagramFailed: 'The diagram could not be drawn.',
  copy: 'Copy',
  copied: 'Copied',
  selected: 'Text selected',
  artifact: {
    source_johku_staff: 'Prepared for you by Johku',
    source_schedule: 'Scheduled report by Johku Max',
    source_merchant: 'Created with Johku Max',
    dismiss: 'Dismiss',
    restore: 'Restore',
    dismissed: 'You dismissed this. It stays available here until it expires.',
    unread: 'New'
  },
  ui: {
    selectAll: 'Select all rows',
    selectRow: 'Select row',
    noRows: 'Nothing to show.',
    working: 'Working…',
    actions: 'Actions',
    confirm: 'Confirm',
    cancel: 'Cancel',
    failed: 'Something went wrong. Try again.',
    previous: 'Previous',
    next: 'Next',
    more: 'Show more',
    typeToSearch: 'Type to search',
    today: 'Today',
    increase: 'Increase',
    decrease: 'Decrease',
    chooseDate: 'Choose date',
    starting: 'Starting',
    ending: 'Ending',
    ongoing: 'Ongoing'
  },
  catalog: { ...CATALOG_VALUE_LABELS }
}

export function mergeLabels (overrides?: Partial<MaxLabels> | null): MaxLabels {
  if (!overrides) return DEFAULT_LABELS
  return {
    ...DEFAULT_LABELS,
    ...overrides,
    severity: { ...DEFAULT_LABELS.severity, ...(overrides.severity ?? {}) },
    kind: { ...DEFAULT_LABELS.kind, ...(overrides.kind ?? {}) },
    effort: { ...DEFAULT_LABELS.effort, ...(overrides.effort ?? {}) },
    impact: { ...DEFAULT_LABELS.impact, ...(overrides.impact ?? {}) },
    artifact: { ...DEFAULT_LABELS.artifact, ...(overrides.artifact ?? {}) },
    ui: { ...DEFAULT_LABELS.ui, ...(overrides.ui ?? {}) },
    catalog: { ...DEFAULT_LABELS.catalog, ...(overrides.catalog ?? {}) }
  }
}

export function fillLabel (template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, key) => (key in values ? String(values[key]) : m))
}
